import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGcsStore } from '../../store/useGcsStore';
import { useSelectedGcsConfigsStore } from '../../store/useSelectedGcsConfigsStore';
import { AsyncAction } from '../../types/general/asyncAction';
import { createLogger } from '../../utils/logger';
import { Spinner } from './Spinner';

const logger = createLogger('InitialRouteHandler');

interface InitialRouteHandlerProps {
  children: React.ReactNode;
}

export function InitialRouteHandler({ children }: InitialRouteHandlerProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = useGcsStore((state) => state.isLoggedIn);
  const connectAll = useGcsStore((state) => state.connectAll);
  const [checkingState, setCheckingState] = useState<AsyncAction<void>>(AsyncAction.loading());
  const hasRunEffect = useRef(false);

  const onSignedIn = useCallback(() => {
    logger.info('onSignedIn called, current path:', location.pathname);
    connectAll();
    if (location.pathname !== '/') {
      logger.info('Navigating to dashboard from', location.pathname);
      navigate('/', { replace: true });
    }
  }, [connectAll, location.pathname, navigate]);

  const onSignedOut = useCallback(() => {
    logger.info('onSignedOut called, current path:', location.pathname);
    if (location.pathname !== '/sign-in') {
      logger.info('Navigating to sign-in from', location.pathname);
      navigate('/sign-in', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (hasRunEffect.current) return;
    hasRunEffect.current = true;
    logger.info('location.pathname', location.pathname);

    // Skip check for callback routes
    if (location.pathname === '/okta-callback') {
      setTimeout(() => setCheckingState(AsyncAction.success()), 0);
      return;
    }

    const selectedConfigs = useSelectedGcsConfigsStore.getState().selectedConfigs;

    if (selectedConfigs.length === 0) {
      logger.warn('No selected configs found, current path:', location.pathname);
      setTimeout(() => setCheckingState(AsyncAction.success()), 0);
      if (location.pathname !== '/sign-in') {
        logger.info('Navigating to sign-in due to no selected configs from', location.pathname);
        navigate('/sign-in', { replace: true });
      }
    } else {
      // Check if user is logged in
      logger.info('Checking login status for GCS:', selectedConfigs[0].id);
      isLoggedIn(selectedConfigs[0].id)
        .then((loggedIn) => {
          logger.info('Login status result:', loggedIn);
          setCheckingState(AsyncAction.success());
          if (loggedIn) {
            onSignedIn();
          } else {
            onSignedOut();
          }
        })
        .catch((error) => {
          logger.error('Login check failed:', error);
          setCheckingState(AsyncAction.failure(error));
          onSignedOut();
        });
    }
  }, [isLoggedIn, location.pathname, navigate, onSignedIn, onSignedOut]);

  if (checkingState.isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw'
        }}
      >
        <Spinner size={36} />
      </div>
    );
  }

  return <>{children}</>;
}
